import { useState, useEffect } from "react";
import BaseModal from "../../BaseComponents/BaseModal/BaseModal";
import ToggleSwitch from "../../BaseComponents/Toggle/ToggleSwitch";
import styles from "./AddAccountModal.module.css";

const emptyForm = {
  name: "",
  type: "bank",
  accountNumber: "",
  currency: "EUR",
  accountUsage: "both",
  active: true,
  linkedPaymentMethods: []
};

const toForm = (data) => {
  if (!data) return emptyForm;
  // Handle both camelCase (sample data / frontend) and snake_case (real API responses)
  return {
    name: data.name || data.account_name || "",
    type: data.type || (data.account_type === 'bank_account' ? 'bank' : data.account_type === 'card_processing' ? 'card' : data.account_type) || "bank",
    accountNumber: data.accountNumber || data.account_number || "",
    currency: data.currency || "EUR",
    accountUsage: data.accountUsage || data.account_usage || "both",
    active: data.active ?? true,
    linkedPaymentMethods: data.linkedPaymentMethods || data.linked_payment_methods || []
  };
};

const AddAccountModal = ({ open, onClose, mode = "add", initialData, onSave, onActiveToggle, saveLoading = false }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  const accountTypes = [
    { value: "bank", label: "Bank Account" },
    { value: "cash", label: "Cash" },
    { value: "card", label: "Card Processing" }
  ];

  const currencies = ["EUR", "USD", "GBP", "CHF"];

  const paymentMethodsByType = {
    bank: ["Bank Transfer", "SEPA", "SWIFT", "Direct Debit"],
    cash: ["Cash"],
    card: ["Visa", "Mastercard", "Amex", "Stripe", "PayPal", "Payment Terminal"]
  };

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialData) {
      setForm(toForm(initialData));
    } else if (mode === "add") {
      setForm(emptyForm);
    }
    setErrors({});
  }, [open, mode, initialData]);

  const handleChange = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // Reset payment methods when type changes
      if (key === "type") {
        next.linkedPaymentMethods = [];
      }
      return next;
    });
    // Clear error for this field
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const togglePaymentMethod = (method) => {
    setForm((prev) => {
      const current = prev.linkedPaymentMethods || [];
      const next = current.includes(method)
        ? current.filter((m) => m !== method)
        : [...current, method];
      return { ...prev, linkedPaymentMethods: next };
    });
    if (errors.linkedPaymentMethods) {
      setErrors((prev) => ({ ...prev, linkedPaymentMethods: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = "Account name is required";
    }

    if (!form.accountNumber.trim()) {
      newErrors.accountNumber = "Account number is required";
    }

    if (form.linkedPaymentMethods.length === 0) {
      newErrors.linkedPaymentMethods = "Select at least one payment method";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSave(form);
    }
  };

  const footer = (
    <>
      <button onClick={onClose} className={styles.cancelBtn} disabled={saveLoading}>
        Cancel
      </button>
      <button onClick={handleSubmit} className={styles.saveBtn} disabled={saveLoading}>
        {saveLoading ? "Saving..." : mode === "add" ? "Add Account" : "Update Account"}
      </button>
    </>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={mode === "add" ? "Add New Account" : "Edit Account"}
      footer={footer}
      width="600px"
    >
      <div className={styles.form}>
        {/* Account Name */}
        <div className={styles.formGroup}>
          <label>
            Account Name <span className={styles.required}>*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="e.g., Main Business Account"
            className={errors.name ? styles.inputError : ""}
          />
          {errors.name && <span className={styles.errorText}>{errors.name}</span>}
        </div>

        {/* Account Type */}
        <div className={styles.formGroup}>
          <label>
            Account Type <span className={styles.required}>*</span>
          </label>
          <div className={styles.typeGrid}>
            {accountTypes.map((type) => (
              <label
                key={type.value}
                className={`${styles.typeOption} ${form.type === type.value ? styles.typeActive : ""}`}
              >
                <input
                  type="radio"
                  name="type"
                  value={type.value}
                  checked={form.type === type.value}
                  onChange={(e) => handleChange("type", e.target.value)}
                />
                <span>{type.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Account Usage */}
        <div className={styles.formGroup}>
          <label>
            Account Usage <span className={styles.required}>*</span>
          </label>
          <div className={styles.typeGrid}>
            {[
              { value: "client", label: "Client Payments" },
              { value: "supplier", label: "Supplier Payments" },
              { value: "both", label: "Both" }
            ].map((opt) => (
              <label
                key={opt.value}
                className={`${styles.typeOption} ${form.accountUsage === opt.value ? styles.typeActive : ""}`}
              >
                <input
                  type="radio"
                  name="accountUsage"
                  value={opt.value}
                  checked={form.accountUsage === opt.value}
                  onChange={(e) => handleChange("accountUsage", e.target.value)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Account Number */}
        <div className={styles.formGroup}>
          <label>
            Account Number / Reference <span className={styles.required}>*</span>
          </label>
          <input
            type="text"
            value={form.accountNumber}
            onChange={(e) => handleChange("accountNumber", e.target.value)}
            placeholder={
              form.type === "bank" ? "e.g., BE68 5390 0754 7034" :
              form.type === "cash" ? "e.g., CASH-001" :
              "e.g., CC-STRIPE-001"
            }
            className={errors.accountNumber ? styles.inputError : ""}
          />
          {errors.accountNumber && <span className={styles.errorText}>{errors.accountNumber}</span>}
        </div>

        {/* Currency */}
        <div className={styles.formGroup}>
          <label>
            Currency <span className={styles.required}>*</span>
          </label>
          <select
            value={form.currency}
            onChange={(e) => handleChange("currency", e.target.value)}
          >
            {currencies.map((currency) => (
              <option key={currency} value={currency}>
                {currency}
              </option>
            ))}
          </select>
        </div>

        {/* Linked Payment Methods */}
        <div className={styles.formGroup}>
          <label>
            Linked Payment Methods <span className={styles.required}>*</span>
          </label>
          <div className={styles.checkboxGroup}>
            {paymentMethodsByType[form.type].map((method) => (
              <label key={method} className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={form.linkedPaymentMethods.includes(method)}
                  onChange={() => togglePaymentMethod(method)}
                />
                <span>{method}</span>
              </label>
            ))}
          </div>
          {errors.linkedPaymentMethods && (
            <span className={styles.errorText}>{errors.linkedPaymentMethods}</span>
          )}
        </div>

        {/* Active Toggle */}
        <div className={styles.formGroup}>
          <label className={styles.toggleLabel}>
            <span>Active</span>
            <ToggleSwitch
              checked={form.active}
              onChange={() => {
                const next = !form.active;
                handleChange("active", next);
                // In edit mode, persist the status change immediately (like the
                // list toggle). In add mode there's no account yet, so it's just
                // form state saved on create.
                if (mode === "edit" && initialData?.id && onActiveToggle) {
                  onActiveToggle(initialData.id, next);
                }
              }}
            />
          </label>
        </div>

        {/* Info Box */}
        <div className={styles.infoBox}>
          <p><strong>Note:</strong> The account balance will be automatically calculated based on transactions. Initial balance is set to €0.00.</p>
        </div>
      </div>
    </BaseModal>
  );
};

export default AddAccountModal;